<template>
  <div>
    <!-- Проверка доступа для агентов -->
    <div v-if="isAgent && !hasAccessToMaterials">
      <!-- Статус approved_by_company -->
      <div v-if="agentCompany && agentCompany.check_status === 'approved_by_company'" class="mb-4">
        <b-alert show variant="info" class="border-0 shadow-sm">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <i class="fas fa-info-circle fa-2x text-info"></i>
            </div>
            <div>
              <h5 class="alert-heading mb-2">Почти готово! Остался последний шаг</h5>
              <p class="mb-2">Для получения доступа ко всем разделам, включая видео-уроки и обучающие материалы, необходимо подписать Договор оферты.</p>
              <b-button
                variant="success"
                size="sm"
                @click="$router.push('/dashboard')"
              >
                <i class="fas fa-file-contract mr-2"></i>
                Перейти к подписанию оферты
              </b-button>
            </div>
          </div>
        </b-alert>
      </div>

      <!-- Остальные статусы (кроме approved) -->
      <div v-else class="mb-4">
        <b-alert show variant="warning" class="border-0 shadow-sm">
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <i class="fas fa-lock fa-2x text-warning"></i>
            </div>
            <div>
              <h5 class="alert-heading mb-2">Доступ к материалам временно ограничен</h5>
              <p class="mb-2">Для получения доступа к разделу необходимо заполнить карточку агента, дождаться подтверждения с нашей стороны и подписать договор оферты.</p>
              <b-button
                variant="primary"
                size="sm"
                @click="$router.push('/profile')"
              >
                <i class="fas fa-user-edit mr-2"></i>
                Заполнить карточку агента
              </b-button>
            </div>
          </div>
        </b-alert>
      </div>

      <!-- Состояние загрузки -->
      <div v-if="lessonsLoading" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="sr-only">Загрузка...</span>
        </div>
        <h5 class="text-muted">Загружаем доступные материалы и уроки...</h5>
      </div>

      <!-- Состояние отсутствия уроков -->
      <div v-else-if="lessonsForMixedContent.length === 0" class="text-center py-5">
        <div class="mb-3">
          <i class="fas fa-book-open fa-3x text-muted"></i>
        </div>
        <h5 class="text-muted">Материалы и уроки на данный момент отсутствуют.</h5>
        <p class="text-muted">Попробуйте обновить страницу позже.</p>
      </div>

      <!-- Смешанный контент: первое видео доступно, остальные недоступны -->
      <div v-else>
        <h4 class="video-materials-title mb-3">
          <!-- <i class="fas fa-video mr-2"></i> -->
          Видео материалы
        </h4>
        <div class="lessons-grid">
        <div class="lesson-card" v-for="lesson in lessonsForMixedContent" :key="lesson.id" :class="{ 'disabled': !lesson.isAccessible }">
          <div class="lesson-image" @click="lesson.isAccessible && startLesson(lesson)">
            <img :src="lesson.image_url" :alt="lesson.title" class="img-fluid">
            <div class="lesson-overlay">
              <i v-if="lesson.isAccessible" class="fas fa-play-circle fa-3x text-white"></i>
              <i v-else class="fas fa-lock fa-2x text-white"></i>
              <div v-if="!lesson.isAccessible" class="lock-message">
                <p class="mb-0" v-if="lesson.video_url === '#'">Видео будет доступно совсем скоро</p>
                <p class="mb-0" v-else>Видео будет доступно<br>после подписания оферты</p>
              </div>
            </div>
          </div>
          <div class="lesson-content">
            <h5 class="lesson-title">{{ lesson.title }}</h5>
            <p class="lesson-description">{{ lesson.description }}</p>
            <div class="lesson-duration">
              <i class="fas fa-clock mr-1"></i>
              {{ lesson.duration }}
            </div>
            <div class="lesson-buttons">
              <!-- Кнопка "К просмотру" - всегда видна, но может быть неактивна -->
              <b-button
                variant="primary"
                size="sm"
                class="mr-2"
                :disabled="!lesson.isAccessible"
                @click="startLesson(lesson)"
              >
                <i class="fas fa-play mr-2"></i>
                К просмотру
              </b-button>
              
              <!-- Кнопка "Просмотрено" только для доступных уроков -->
              <b-button
                v-if="lesson.isAccessible"
                variant="success"
                size="sm"
                :disabled="!canMarkAsCompleted(lesson.id)"
                @click="completeLesson(lesson.id)"
              >
                <i class="fas fa-check mr-2"></i>
                Просмотрено
              </b-button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Основной контент для approved агентов -->
    <div v-else-if="isAgent && hasAccessToMaterials">
      <b-alert show variant="success" class="mb-4 border-0 shadow-sm">
        <div class="d-flex align-items-center">
          <div class="mr-3 d-none d-md-block">
            <i class="fas fa-check-circle fa-2x text-success"></i>
          </div>
          <div>
            <h5 class="alert-heading mb-2">Добро пожаловать в раздел обучения и полезных материалов!</h5>
            <p class="mb-0">Изучайте материалы, повышайте квалификацию учитесь больше продавать и развивайте свои навыки в работе с системой TANASOFT.</p>
          </div>
        </div>
      </b-alert>

      <!-- Блок с памятками -->
      <div class="memories-section mb-4">
        <h4 class="memories-title">
          <!-- <i class="fas fa-bookmark mr-2"></i> -->
          Памятки
        </h4>
        <div class="memories-grid">
          <div 
            v-for="memory in memories" 
            :key="memory.id" 
            class="memory-card"
            @click="openMemory(memory)"
          >
            <div class="memory-icon">
              <i :class="memory.icon"></i>
            </div>
            <div class="memory-content">
              <h6 class="memory-title">{{ memory.title }}</h6>
              <p class="memory-description">{{ memory.description }}</p>
            </div>
            <div class="memory-action">
              <i class="fas fa-download"></i>
            </div>
          </div>
        </div>
      </div>    

      <!-- Состояние загрузки -->
      <div v-if="lessonsLoading" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="sr-only">Загрузка...</span>
        </div>
        <h5 class="text-muted">Загружаем доступные материалы и уроки...</h5>
      </div>

      <!-- Состояние отсутствия уроков -->
      <div v-else-if="lessonsWithAccess.length === 0" class="text-center py-5">
        <div class="mb-3">
          <i class="fas fa-book-open fa-3x text-muted"></i>
        </div>
        <h5 class="text-muted">Материалы и уроки на данный момент отсутствуют.</h5>
        <p class="text-muted">Попробуйте обновить страницу позже.</p>
      </div>

      <!-- Основной контент для approved агентов -->
      <div v-else>
        <h4 class="video-materials-title mb-3">
          <!-- <i class="fas fa-video mr-2"></i> -->
          Видео материалы
        </h4>
        <div class="lessons-grid">
        <div class="lesson-card" v-for="lesson in lessonsWithAccess" :key="lesson.id">
          <div class="lesson-image" @click="lesson.isAccessible && startLesson(lesson)">
            <img :src="lesson.image_url" :alt="lesson.title" class="img-fluid">
            <div class="lesson-overlay">
              <i v-if="lesson.isAccessible" class="fas fa-play-circle fa-3x text-white"></i>
              <i v-else class="fas fa-lock fa-2x text-white"></i>
              <div v-if="!lesson.isAccessible" class="lock-message">
                <p class="mb-0" v-if="lesson.video_url === '#'">Видео будет доступно совсем скоро</p>
                <p class="mb-0" v-else>Видео будет доступно<br>после просмотра предыдущего</p>
              </div>
            </div>
          </div>
          <div class="lesson-content">
            <h5 class="lesson-title">{{ lesson.title }}</h5>
            <p class="lesson-description">{{ lesson.description }}</p>
            <div class="lesson-duration">
              <i class="fas fa-clock mr-1"></i>
              {{ lesson.duration }}
            </div>
            <div class="lesson-buttons">
              <!-- Кнопка "К просмотру" -->
              <b-button
                variant="primary"
                size="sm"
                class="mr-2"
                :disabled="!lesson.isAccessible"
                @click="startLesson(lesson)"
              >
                <i class="fas fa-play mr-2"></i>
                К просмотру
              </b-button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Контент для всех ролей кроме агентов (все видео доступны) -->
    <div v-else>
      <b-alert show variant="success" class="mb-4 border-0 shadow-sm">
        <div class="d-flex align-items-center">
          <div class="mr-3 d-none d-md-block">
            <i class="fas fa-graduation-cap fa-2x text-success"></i>
          </div>
          <div>
            <h5 class="alert-heading mb-2">Добро пожаловать в раздел обучения и полезных материалов!</h5>
            <p class="mb-0">Изучайте все доступные материалы и повышайте свою квалификацию. Все видео-уроки доступны для просмотра.</p>
          </div>
        </div>
      </b-alert>


      <!-- Блок с памятками -->
      <div class="memories-section mb-4">
        <h4 class="memories-title">
          <!-- <i class="fas fa-bookmark mr-2"></i> -->
          Памятки
        </h4>
        <div class="memories-grid">
          <div 
            v-for="memory in memories" 
            :key="memory.id" 
            class="memory-card"
            @click="openMemory(memory)"
          >
            <div class="memory-icon">
              <i :class="memory.icon"></i>
            </div>
            <div class="memory-content">
              <h6 class="memory-title">{{ memory.title }}</h6>
              <p class="memory-description">{{ memory.description }}</p>
            </div>
            <div class="memory-action">
              <i class="fas fa-download"></i>
            </div>
          </div>
        </div>
      </div>    

      <!-- Состояние загрузки -->
      <div v-if="lessonsLoading" class="text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="sr-only">Загрузка...</span>
        </div>
        <h5 class="text-muted">Загружаем доступные материалы и уроки...</h5>
      </div>

      <!-- Состояние отсутствия уроков -->
      <div v-else-if="allLessonsUnlocked.length === 0" class="text-center py-5">
        <div class="mb-3">
          <i class="fas fa-book-open fa-3x text-muted"></i>
        </div>
        <h5 class="text-muted">Материалы и уроки на данный момент отсутствуют.</h5>
        <p class="text-muted">Попробуйте обновить страницу позже.</p>
      </div>

      <!-- Контент для всех ролей кроме агентов (все видео доступны) -->
      <div v-else>
        <h4 class="video-materials-title mb-3">
          <!-- <i class="fas fa-video mr-2"></i> -->
          Видео материалы
        </h4>
        <div class="lessons-grid">
        <div class="lesson-card" v-for="lesson in allLessonsUnlocked" :key="lesson.id">
          <div class="lesson-image" @click="startLesson(lesson)">
            <img :src="lesson.image_url" :alt="lesson.title" class="img-fluid">
            <div class="lesson-overlay">
              <i class="fas fa-play-circle fa-3x text-white"></i>
            </div>
          </div>
          <div class="lesson-content">
            <h5 class="lesson-title">{{ lesson.title }}</h5>
            <p class="lesson-description">{{ lesson.description }}</p>
            <div class="lesson-duration">
              <i class="fas fa-clock mr-1"></i>
              {{ lesson.duration }}
            </div>
            <div class="lesson-buttons">
              <!-- Кнопка "К просмотру" -->
              <b-button
                variant="primary"
                size="sm"
                class="mr-2"
                @click="startLesson(lesson)"
              >
                <i class="fas fa-play mr-2"></i>
                К просмотру
              </b-button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно с видеоплеером -->
    <b-modal
      v-model="showVideoPlayer"
      :title="currentLesson ? currentLesson.title : 'Видео урок'"
      size="xl"
      :hide-footer="true"
      :hide-header-close="false"
      class="video-player-modal"
      @hidden="onVideoModalHidden"
      @shown="onVideoModalShown"
      :no-close-on-backdrop="false"
      :no-close-on-esc="false"
    >
      <div v-if="currentLesson" class="video-player-container" @keydown="handleKeydown">
        <!-- Видеоплеер -->
        <div class="video-wrapper">
          <video
            ref="videoPlayer"
            :src="currentLesson.video_url"
            controls
            preload="metadata"
            class="video-player"
            :aria-label="`Видео урок: ${currentLesson.title}`"
            @loadedmetadata="onVideoLoaded"
            @timeupdate="onVideoTimeUpdate"
            @ended="onVideoEnded"
            @play="onVideoPlay"
            @pause="onVideoPause"
            @error="onVideoError"
            @loadstart="onVideoLoadStart"
            @canplay="onVideoCanPlay"
            @dblclick="toggleFullscreen"
            controlsList="nodownload"
            disablePictureInPicture
            oncontextmenu="return false;"
          >
            <!-- Поддержка различных форматов видео -->
            <source :src="currentLesson.video_url" type="video/mp4">
            <source :src="currentLesson.video_url" type="video/webm">
            <source :src="currentLesson.video_url" type="video/ogg">
            Ваш браузер не поддерживает воспроизведение видео.
          </video>
          
          <!-- Индикатор загрузки -->
          <div v-if="videoLoading" class="video-loading">
            <div class="spinner-border text-light" role="status">
              <span class="sr-only">Загрузка видео...</span>
            </div>
            <p class="mt-2 text-light">Загрузка видео...</p>
          </div>
          
          <!-- Сообщение об ошибке -->
          <div v-if="videoError" class="video-error">
            <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h5 class="text-warning">Ошибка загрузки видео</h5>
            <p class="text-light">{{ videoErrorMessage }}</p>
            <b-button variant="warning" @click="retryVideoLoad">
              <i class="fas fa-redo mr-2"></i>
              Попробовать снова
            </b-button>
          </div>
          
          <!-- Кнопка полноэкранного режима -->
          <button 
            class="fullscreen-btn"
            @click="toggleFullscreen"
            :title="isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'"
            aria-label="Переключить полноэкранный режим"
          >
            <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
          </button>
        </div>
        
        <!-- Информация об уроке -->
        <div class="lesson-info mt-4">
          <h5>{{ currentLesson.title }}</h5>
          <p class="text-muted">{{ currentLesson.description }}</p>
          <div class="lesson-meta">
            <div>
              <span class="badge badge-success mr-2">
                <i class="fas fa-clock mr-1"></i>
                {{ currentLesson.duration }}
              </span>
            </div>
            <!-- Кнопки управления -->
            <div class="video-controls text-right">
              <b-button
                :variant="isLessonCompleted(currentLesson.id) ? 'info' : 'success'"
                size="md"
                @click="completeLesson(currentLesson.id)"
                :disabled="!canMarkAsCompleted(currentLesson.id)"
                class="mr-2"
              >
                <span class="d-none d-md-block"><i :class="isLessonCompleted(currentLesson.id) ? 'fas fa-check-circle' : 'fas fa-check'" class="mr-1"></i> {{ isLessonCompleted(currentLesson.id) ? 'Уже просмотрено' : 'Отметить как просмотренный' }}</span>
                <span class="d-block d-md-none">{{ isLessonCompleted(currentLesson.id) ? 'Просмотрено' : 'Просмотрели?' }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { API_LESSONS, API_MEMORIES } from "../constants";

export default {
  data() {
    return {
      lessons: [], // Уроки загружаются из БД
      lessonViews: {}, // Статус просмотра уроков
      loading: false,
      lessonsLoading: false, // Состояние загрузки уроков
      showVideoPlayer: false, // Флаг для отображения модального окна видеоплеера
      currentLesson: null, // Текущий урок, который отображается в модальном окне
      videoPlayer: null, // Ссылка на плеер
      videoStartTime: 0, // Время начала просмотра видео
      videoEndTime: 0, // Время окончания просмотра видео
      videoDuration: 0, // Общая длительность видео
      videoLoading: false, // Флаг загрузки видео
      videoError: false, // Флаг ошибки загрузки видео
      videoErrorMessage: '', // Сообщение об ошибке загрузки видео
      isFullscreen: false, // Флаг для отслеживания полноэкранного режима
      currentTime: 0, // Текущее время просмотра видео
      memories: [], // Памятки загружаются из БД
      memoriesLoading: false // Состояние загрузки памяток
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser", "isAuthenticated"]),

    agentCompany() {
      return this.$store.getters['auth/getAgentCompany'];
    },

    isAgent() {
      return this.currentUser.role.includes('agent');
    },

    hasAccessToMaterials() {
      if (!this.agentCompany) return false;

      return this.agentCompany.check_status === 'approved';
    },

    agentCompanyStatusLabel() {
      if (!this.agentCompany) return 'Не указано';

      const statusLabels = {
        'fill': 'На заполнении',
        'pending': 'На проверке',
        'approved': 'Подписан договор оферты',
        'approved_by_company': 'Агент проверен',
        'rejected': 'Отклонён',
        'revision': 'На доработке',
        'termination_by_agent': 'Расторжение договора по желанию агента',
        'termination_by_company': 'Расторжение договора по желанию компании'
      };

      return statusLabels[this.agentCompany.check_status] || 'Не указано';
    },

    // Определяем доступность каждого урока на основе прогресса просмотра (для approved агентов)
    lessonsWithAccess() {
      return this.lessons.map((lesson, index) => {
        let isAccessible = false;
        
        // Если video_url равен "#", урок всегда заблокирован
        if (lesson.video_url === '#') {
          isAccessible = false;
        } else if (index === 0) {
          // Первый урок всегда доступен (если video_url не "#")
          isAccessible = true;
        } else {
          // Остальные уроки доступны только если предыдущий завершен
          const previousLesson = this.lessons[index - 1];
          if (previousLesson) {
            const previousLessonView = this.lessonViews[previousLesson.id];
            isAccessible = !!(previousLessonView && previousLessonView.completed_at);
            console.log(`Урок ${lesson.id} (${lesson.title}): предыдущий урок ${previousLesson.id} завершен: ${isAccessible}`);
          }
        }
        
        return {
          ...lesson,
          isAccessible,
          isFirst: index === 0, // Первый урок
          isLast: index === this.lessons.length - 1 // Последний урок
        };
      });
    },

    // Для не-агентов: только первое видео доступно
    lessonsForNonAgents() {
      return this.lessons.map((lesson, index) => ({
        ...lesson,
        isAccessible: index === 0, // Только первый урок доступен
        isFirst: index === 0,
        isLast: index === this.lessons.length - 1
      }));
    },

    // Для смешанного контента: только первое видео доступно
    lessonsForMixedContent() {
      return this.lessons.map((lesson, index) => ({
        ...lesson,
        isAccessible: index === 0 && lesson.video_url !== '#', // Только первый урок доступен и video_url не "#"
        isFirst: index === 0,
        isLast: index === this.lessons.length - 1
      }));
    },

    // Для всех ролей кроме агентов: все видео доступны без блокировок
    allLessonsUnlocked() {
      return this.lessons.map((lesson, index) => ({
        ...lesson,
        isAccessible: lesson.video_url !== '#', // Все уроки доступны, кроме тех где video_url равен "#"
        isFirst: index === 0,
        isLast: index === this.lessons.length - 1
      }));
    }
  },

  methods: {
    // Загрузить уроки из БД
    async loadLessons() {
      this.lessonsLoading = true;
      try {
        const response = await api.call('get', API_LESSONS);
        if (response.data.success) {
          this.lessons = response.data.data.map(lesson => ({
            ...lesson,
            isFirst: lesson.is_first,
            isLast: lesson.is_last
          }));
        }
      } catch (error) {
        console.error('Ошибка при загрузке уроков:', error);
        // В случае ошибки оставляем пустой массив
        this.lessons = [];
      } finally {
        this.lessonsLoading = false;
      }
    },

        // Загрузить статические уроки (fallback) - больше не используется
    loadStaticLessons() {
      // Оставляем пустой массив - уроки должны загружаться только из БД
      this.lessons = [];
    },

    // Начать просмотр урока
    async startLesson(lesson) {
      if (this.loading) return;
      
      // Блокируем воспроизведение если video_url равен "#"
      if (lesson.video_url === '#') {
        this.$bvToast.toast('Видео будет доступно совсем скоро', {
          variant: 'info',
          title: 'Информация'
        });
        return;
      }
      
      console.log('startLesson вызван для урока:', lesson.id, lesson.title);
      console.log('Текущий статус урока:', this.lessonViews[lesson.id]);
      
      // Проверяем, не был ли урок уже начат
      if (this.lessonViews[lesson.id] && this.lessonViews[lesson.id].started_at) {
        console.log('Урок уже начат, просто открываем видеоплеер');
        // Урок уже начат, просто открываем видеоплеер
        this.openVideoPlayer(lesson);
        return;
      }
      
      console.log('Начинаем новый урок');
      this.loading = true;
      try {
        const response = await api.call('post', API_LESSONS + '/start', {
          lesson_id: lesson.id
        });
        
        if (response.data.success) {
          this.lessonViews[lesson.id] = {
            ...this.lessonViews[lesson.id],
            started_at: new Date().toISOString()
          };
          
          this.$bvToast.toast(`Начат просмотр урока: ${lesson.title}`, {
            variant: 'info',
            title: 'Урок'
          });
          
          // Открываем видеоплеер
          this.openVideoPlayer(lesson);
        }
      } catch (error) {
        console.error('Ошибка при начале просмотра урока:', error);
        this.$bvToast.toast('Ошибка при начале просмотра урока', {
          variant: 'danger',
          title: 'Ошибка'
        });
      } finally {
        this.loading = false;
      }
    },

    // Завершить просмотр урока
    async completeLesson(lessonId) {
      if (this.loading) return;
      
      this.loading = true;
      try {
        const response = await api.call('post', API_LESSONS + '/complete', {
          lesson_id: lessonId
        });
        
        if (response.data.success) {
          // Обновляем состояние реактивно
          this.$set(this.lessonViews, lessonId, {
            ...this.lessonViews[lessonId],
            completed_at: new Date().toISOString()
          });
          
          this.$bvToast.toast('Урок отмечен как просмотренный!', {
            variant: 'success',
            title: 'Успех'
          });
          
          // Перезагружаем статус уроков для получения актуальных данных
          await this.loadLessonStatus();
          
          // Ждем обновления computed свойств
          await this.$nextTick();
          
          // Если это не последний урок, открываем следующий
          const currentLesson = this.lessonsWithAccess.find(l => l.id === lessonId);
          if (currentLesson && !currentLesson.isLast) {
            const nextLesson = this.lessonsWithAccess.find(l => l.sort_order === currentLesson.sort_order + 1);
            console.log('Следующий урок:', nextLesson);
            
            if (nextLesson && nextLesson.isAccessible) {
              console.log('Следующий урок доступен, открываем его');
              // Закрываем текущий видеоплеер
              this.closeVideoPlayer();
              // Небольшая задержка для корректного обновления UI
              setTimeout(() => {
                this.startLesson(nextLesson);
              }, 300);
            } else {
              console.log('Следующий урок недоступен, закрываем плеер');
              this.closeVideoPlayer();
            }
          } else {
            console.log('Это последний урок, закрываем плеер');
            // Закрываем видеоплеер если это последний урок
            this.closeVideoPlayer();
          }
        }
      } catch (error) {
        console.error('Ошибка при завершении просмотра урока:', error);
        this.$bvToast.toast('Ошибка при завершении просмотра урока', {
          variant: 'danger',
          title: 'Ошибка'
        });
      } finally {
        this.loading = false;
      }
    },

    // Проверить, можно ли отметить урок как просмотренный
    canMarkAsCompleted(lessonId) {
      const lessonView = this.lessonViews[lessonId];
      if (!lessonView) return false;
      
      // Урок должен быть начат и не завершен
      const canComplete = lessonView.started_at && !lessonView.completed_at;
      console.log(`canMarkAsCompleted для урока ${lessonId}:`, canComplete, 'lessonView:', lessonView);
      return canComplete;
    },

    // Открыть урок (заглушка)
    openLesson(lesson) {
      this.$bvToast.toast(`Открывается урок: ${lesson.title}`, {
        variant: 'info',
        title: 'Урок'
      });
    },

    // Загрузить статус просмотра уроков
    async loadLessonStatus() {
      try {
        const response = await api.call('get', API_LESSONS + '/status');
        if (response.data.success) {
          // Обновляем lessonViews реактивно
          this.lessonViews = { ...response.data.data };
          console.log('Статус уроков обновлен:', this.lessonViews);
        }
      } catch (error) {
        console.error('Ошибка при загрузке статуса уроков:', error);
      }
    },

    // Обработчик загрузки видео
    onVideoLoaded() {
      if (this.$refs.videoPlayer) {
        this.videoDuration = this.$refs.videoPlayer.duration;
        this.currentTime = this.$refs.videoPlayer.currentTime;
        this.updateProgress();
      }
    },

    // Обработчик обновления времени просмотра
    onVideoTimeUpdate() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime;
        this.updateProgress();
      }
    },

    // Обработчик окончания просмотра видео
    onVideoEnded() {
      this.currentTime = this.$refs.videoPlayer.duration;
      this.updateProgress();
      
      // Автоматически отмечаем урок как просмотренный только если он еще не был завершен
      if (this.currentLesson && !this.isLessonCompleted(this.currentLesson.id)) {
        console.log('Видео закончилось, автоматически завершаем урок:', this.currentLesson.id);
        this.completeLesson(this.currentLesson.id);
      }
    },

    // Обработчик начала просмотра видео
    onVideoPlay() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime;
        this.updateProgress();
      }
    },

    // Обработчик паузы видео
    onVideoPause() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime;
        this.updateProgress();
      }
    },

    // Обработчик ошибки загрузки видео
    onVideoError(event) {
      this.videoError = true;
      this.videoErrorMessage = event.target.error ? event.target.error.message : 'Неизвестная ошибка загрузки видео.';
      console.error('Ошибка загрузки видео:', event.target.error);
    },

    // Обработчик начала загрузки видео
    onVideoLoadStart() {
      this.videoLoading = true;
      this.videoError = false;
      this.videoErrorMessage = '';
    },

    // Обработчик завершения загрузки видео
    onVideoCanPlay() {
      this.videoLoading = false;
    },

    // Обновить прогресс просмотра
    updateProgress() {
      // Метод оставлен для совместимости, но больше не обновляет процент
      // так как прогресс-бар убран из интерфейса
    },

    // Открыть модальное окно с видеоплеером
    openVideoPlayer(lesson) {
      console.log('openVideoPlayer вызван для урока:', lesson.id, lesson.title);
      console.log('Текущий currentLesson:', this.currentLesson);
      console.log('showVideoPlayer:', this.showVideoPlayer);
      
      // Блокируем открытие видеоплеера если video_url равен "#"
      if (lesson.video_url === '#') {
        this.$bvToast.toast('Видео будет доступно совсем скоро', {
          variant: 'info',
          title: 'Информация'
        });
        return;
      }
      
      // Проверяем, не открыт ли уже этот урок
      if (this.currentLesson && this.currentLesson.id === lesson.id && this.showVideoPlayer) {
        console.log('Урок уже открыт, не открываем повторно');
        return; // Урок уже открыт
      }
      
      console.log('Открываем видеоплеер для урока');
      this.currentLesson = lesson;
      this.showVideoPlayer = true;
      this.currentTime = 0;
      this.videoDuration = 0;
      this.videoLoading = false;
      this.videoError = false;
      this.videoErrorMessage = '';
      
      // Сбрасываем состояние плеера
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.$refs.videoPlayer.currentTime = 0;
          this.$refs.videoPlayer.load();
        }
      });
    },

    // Закрыть модальное окно с видеоплеером
    closeVideoPlayer() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.$refs.videoPlayer.currentTime = 0;
        // Удаляем обработчики защиты
        this.removeDownloadProtection();
      }
      this.showVideoPlayer = false;
      this.currentLesson = null;
      this.currentTime = 0;
      this.videoDuration = 0;
      this.videoLoading = false;
      this.videoError = false;
      this.videoErrorMessage = '';
    },

    // Удалить защиту от скачивания
    removeDownloadProtection() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      
      // Удаляем все обработчики событий
      video.removeEventListener('contextmenu', this.preventDefault);
      video.removeEventListener('dragstart', this.preventDefault);
      video.removeEventListener('selectstart', this.preventDefault);
      video.removeEventListener('copy', this.preventDefault);
      video.removeEventListener('save', this.preventDefault);
      
      // Удаляем глобальный обработчик клавиш
      document.removeEventListener('keydown', this.handleKeydownProtection);
    },

    // Вспомогательная функция для preventDefault
    preventDefault(e) {
      e.preventDefault();
    },

    // Обработчик клавиш для защиты
    handleKeydownProtection(e) {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's' || e.key === 'S') {
          e.preventDefault();
          return false;
        }
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          return false;
        }
      }
    },

    // Обработчик скрытия модального окна
    onVideoModalHidden() {
      this.closeVideoPlayer();
    },

    // Обработчик показа модального окна
    onVideoModalShown() {
      console.log('onVideoModalShown вызван');
      console.log('currentLesson:', this.currentLesson);
      console.log('lessonViews:', this.lessonViews);
      
      // Автоматически запускаем просмотр только если урок еще не был начат
      if (this.currentLesson && this.currentLesson.isAccessible && 
          (!this.lessonViews[this.currentLesson.id] || !this.lessonViews[this.currentLesson.id].started_at)) {
        console.log('Автоматически запускаем startLesson из onVideoModalShown');
        this.startLesson(this.currentLesson);
      } else {
        console.log('startLesson не вызывается автоматически');
      }
      
      // Добавляем дополнительные обработчики для предотвращения скачивания
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.addDownloadProtection();
        }
      });
    },

    // Добавить защиту от скачивания
    addDownloadProtection() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      
      // Отключаем контекстное меню
      video.addEventListener('contextmenu', this.preventDefault);
      
      // Отключаем перетаскивание
      video.addEventListener('dragstart', this.preventDefault);
      
      // Отключаем выделение
      video.addEventListener('selectstart', this.preventDefault);
      
      // Отключаем копирование
      video.addEventListener('copy', this.preventDefault);
      
      // Отключаем сохранение
      video.addEventListener('save', this.preventDefault);
      
      // Отключаем горячие клавиши
      document.addEventListener('keydown', this.handleKeydownProtection);
    },

    // Обработчик клавиатурных сокращений
    handleKeydown(event) {
      const video = this.$refs.videoPlayer;
      if (!video) return;

      if (event.key === ' ') {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      } else if (event.key === 'F') {
        this.toggleFullscreen();
      } else if (event.key === 'ArrowLeft') {
        video.currentTime -= 5;
      } else if (event.key === 'ArrowRight') {
        video.currentTime += 5;
      }
    },

    // Повторить загрузку видео
    retryVideoLoad() {
      this.videoLoading = true;
      this.videoError = false;
      this.videoErrorMessage = '';
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.load();
      }
    },

    // Переключить полноэкранный режим
    toggleFullscreen() {
      const video = this.$refs.videoPlayer;
      if (video) {
        if (!this.isFullscreen) {
          if (video.requestFullscreen) {
            video.requestFullscreen();
          } else if (video.mozRequestFullScreen) { // Firefox
            video.mozRequestFullScreen();
          } else if (video.webkitRequestFullscreen) { // Chrome, Safari and Opera
            video.webkitRequestFullscreen();
          } else if (video.msRequestFullscreen) { // IE/Edge
            video.msRequestFullscreen();
          }
          this.isFullscreen = true;
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.mozCancelFullScreen) { // Firefox
            document.mozCancelFullScreen();
          } else if (document.webkitExitFullscreen) { // Chrome, Safari and Opera
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) { // IE/Edge
            document.msExitFullscreen();
          }
          this.isFullscreen = false;
        }
      }
    },

    // Обработчик изменения полноэкранного режима
    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
    },

    // Проверка, отмечен ли урок как просмотренный
    isLessonCompleted(lessonId) {
      const lessonView = this.lessonViews[lessonId];
      return lessonView && lessonView.completed_at;
    },

    // Загрузить памятки из БД
    async loadMemories() {
      this.memoriesLoading = true;
      try {
        const response = await api.call('get', API_MEMORIES);
        if (response.data.success) {
          this.memories = response.data.data;
        }
      } catch (error) {
        console.error('Ошибка при загрузке памяток:', error);
        // В случае ошибки оставляем пустой массив
        this.memories = [];
      } finally {
        this.memoriesLoading = false;
      }
    },

    // Открыть памятку
    openMemory(memory) {
      // Открываем PDF файл в новой вкладке
      window.open(memory.file_path, '_blank');
    }
  },

  async mounted() {
    // Проверяем доступ при загрузке компонента
    if (this.isAgent) {
      console.log('Agent company status:', this.agentCompany.check_status);
    }
    
    // Загружаем уроки и памятки параллельно
    await Promise.all([
      this.loadLessons(),
      this.loadMemories()
    ]);
    
    // Загружаем статус просмотра уроков
    await this.loadLessonStatus();
    
    // Добавляем слушатели для полноэкранного режима
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('msfullscreenchange', this.onFullscreenChange);
  },

  beforeDestroy() {
    // Удаляем слушатели для полноэкранного режима
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('msfullscreenchange', this.onFullscreenChange);
  }
}
</script>

<style scoped>
.lessons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.lesson-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.lesson-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.lesson-card.disabled {
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  filter: grayscale(0.8);
}

.lesson-card.disabled .lesson-image {
  cursor: not-allowed;
}

.lesson-card.disabled .lesson-image:hover {
  transform: none;
}

.lesson-card.disabled .lesson-overlay {
  opacity: 1;
  background: rgba(0, 0, 0, 0.7);
}

.lesson-card.disabled .lesson-title {
  color: #6c757d;
}

.lesson-card.disabled .lesson-description {
  color: #adb5bd;
}

.lesson-card.disabled .lesson-duration {
  color: #adb5bd;
}

/* Стили для сообщения под замком */
.lock-message {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  text-align: center;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 6px;
  padding: 8px 12px;
  z-index: 5;
}

.lock-message p {
  color: #fff;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.lesson-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.lesson-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.lesson-card:hover .lesson-image img {
  transform: scale(1.05);
}

.lesson-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.lesson-card:hover .lesson-overlay {
  opacity: 1;
}

.lesson-card.disabled .lesson-overlay {
  opacity: 1;
  background: rgba(0, 0, 0, 0.7);
}

.lesson-content {
  padding: 1.5rem;
}

.lesson-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #2c3e50;
  line-height: 1.3;
}

.lesson-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.lesson-duration {
  color: #495057;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.lesson-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.disabled-lessons .lesson-card {
  cursor: not-allowed;
}

.disabled-lessons .lesson-card:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Модальное окно видеоплеера */
.video-player-modal .modal-content {
  background-color: #1a1a1a; /* Темный фон для видеоплеера */
  color: white;
  border-radius: 10px;
  overflow: hidden;
}

.video-player-modal .modal-header {
  background-color: #2c3e50;
  border-bottom: 1px solid #34495e;
  border-radius: 10px 10px 0 0;
}

.video-player-modal .modal-title {
  color: white;
}

.video-player-modal .modal-body {
  padding: 0;
  background-color: #1a1a1a;
}

.video-player-modal .modal-footer {
  background-color: #2c3e50;
  border-top: 1px solid #34495e;
  border-radius: 0 0 10px 10px;
}

.video-player-modal .modal-footer .btn {
  color: white;
  border-color: #34495e;
}

.video-player-modal .modal-footer .btn:hover {
  background-color: #34495e;
  border-color: #34495e;
}

.video-player-modal .modal-footer .btn:focus {
  box-shadow: none;
}

.video-player-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0rem;
}

.video-wrapper {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000; /* Фон для видеоплеера */
  border-radius: 8px;
  overflow: hidden;
  min-height: 400px;
  position: relative;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain; /* Убедимся, что видео масштабируется, но не обрезается */
  border-radius: 8px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
  -khtml-user-select: none;
}

/* Дополнительная защита от скачивания */
.video-player::-webkit-media-controls-download-button {
  display: none !important;
}

.video-player::-webkit-media-controls-enclosure {
  overflow: hidden;
}

.video-player::-webkit-media-controls-panel {
  width: calc(100% + 30px);
}

.video-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.video-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 2rem;
}

.video-error i {
  margin-bottom: 15px;
}

.video-error h5 {
  margin-bottom: 10px;
}

.video-error p {
  color: #bdc3c7;
  margin-bottom: 20px;
  text-align: center;
  max-width: 300px;
}

.video-error .btn {
  min-width: 120px;
  line-height: 40px;
}

.fullscreen-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  border: 1px solid #34495e;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s ease;
}

.fullscreen-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.fullscreen-btn:focus {
  outline: none;
}

.fullscreen-btn i {
  color: white;
  font-size: 1.2rem;
}

.lesson-info {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #e5e5e5;
  border-radius: 8px;
}

.lesson-info h5 {
  margin-bottom: 0.5rem;
}

.lesson-info p {
  color: #bdc3c7;
  margin-bottom: 0.75rem;
}

.lesson-meta {
  margin-top: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.lesson-meta .badge {
  font-size: 0.8rem;
  padding: 0.4rem 0.6rem;
}

.video-controls {
  margin-top: 0rem;
}

.video-controls .btn {
  min-width: 100px;
  margin: 0 0.25rem;
}

.video-controls .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.video-controls .btn-secondary:hover {
  background-color: #5a6268;
  border-color: #5a6268;
}

.video-controls .btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.video-controls .btn-info:hover {
  background-color: #138496;
  border-color: #138496;
}

.video-controls .btn-info:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  opacity: 0.65;
}

/* Стили для кнопок в блоке информации об уроке */
.lesson-info .video-controls {
  margin-top: 0rem;
}

.lesson-info .video-controls .btn {
  min-width: 100px;
  margin: 0 0.25rem;
  font-size: 0.9rem;
}

.lesson-info .video-controls .btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.lesson-info .video-controls .btn-info:hover {
  background-color: #138496;
  border-color: #138496;
}

.lesson-info .video-controls .btn-info:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  opacity: 0.65;
}

/* Улучшения для мобильных устройств */
@media (max-width: 768px) {
  .video-player-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }
  
  .video-wrapper {
    min-height: 250px;
  }
  
  .lesson-info .video-controls .btn {
    min-width: 120px;
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
    margin: 0.25rem;
  }
  
  .lesson-info {
    padding: 0.75rem;
  }
  
  .video-player-container {
    padding: 0.75rem;
  }
  
  /* Адаптация сообщения под замком для мобильных */
  .lock-message {
    bottom: 5px;
    left: 5px;
    right: 5px;
    padding: 6px 8px;
  }
  
  .lock-message p {
    font-size: 0.75rem;
  }
}

/* Анимации для плавного появления */
.video-player-modal .modal-content {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Стили для hover эффектов на карточках уроков */
.lesson-image {
  cursor: pointer;
  transition: all 0.3s ease;
}

.lesson-image:hover {
  transform: scale(1.02);
}

.lesson-card.disabled .lesson-image {
  cursor: not-allowed;
}

.lesson-card.disabled .lesson-image:hover {
  transform: none;
}

/* Дополнительные стили для сетки уроков */
@media (max-width: 768px) {
  .lessons-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .lesson-content {
    padding: 1rem;
  }
  
  .lesson-buttons {
    flex-direction: column;
  }
}

/* Стили для блока памяток */
.memories-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  padding-left: 25px;
}

.memories-title i {
  color: #007bff;
}

/* Стили для подзаголовка видео материалов */
.video-materials-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  padding-left: 25px;
}

.video-materials-title i {
  color: #dc3545;
}

.memories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.memory-card {
  background: white;
  border-radius: 12px;
  padding: 1.2rem;
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 515px;
}

.memory-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.memory-icon {
  margin-right: 1rem;
  font-size: 1.5rem;
  min-width: 2rem;
  text-align: center;
}

.memory-content {
  flex: 1;
}

.memory-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #2c3e50;
  line-height: 1.3;
}

.memory-description {
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0;
  line-height: 1.2;
}

.memory-action {
  margin-left: 1rem;
  color: #007bff;
  font-size: 1.2rem;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.memory-card:hover .memory-action {
  opacity: 1;
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
  .memories-section {
    padding: 1rem;
  }
  
  .memories-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .memory-card {
    padding: 1rem;
  }
  
  .memory-icon {
    font-size: 1.25rem;
    margin-right: 0.75rem;
  }
  
  .memory-title {
    font-size: 0.9rem;
  }
  
  .memory-description {
    font-size: 0.8rem;
  }
}
</style>


